import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { apiGet, apiPost, isBackendConfigured } from '../utils/backendApi';

const TOPICS = [
  ['billing', 'Billing'],
  ['account', 'Account access'],
  ['technical', 'Something broken'],
  ['sales', 'Buying'],
  ['product', 'How the product works'],
];

function when(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString();
}

export default function SupportQueue() {
  const [params] = useSearchParams();
  const [queue, setQueue] = useState(null);
  const [error, setError] = useState('');
  const [routes, setRoutes] = useState({});
  const [fallback, setFallback] = useState('');
  const [replyToVisitor, setReplyToVisitor] = useState(true);
  const [agent, setAgent] = useState('');
  const [agents, setAgents] = useState([]);
  const [mail, setMail] = useState(null);
  const [selected, setSelected] = useState(params.get('ticket') || '');
  const [thread, setThread] = useState(null);
  const [reply, setReply] = useState('');
  const [busy, setBusy] = useState('');
  const [notice, setNotice] = useState('');

  const load = async () => {
    const data = await apiGet('/support/queue');
    setQueue(data.tickets || []);
    setRoutes(data.routes || {});
    setFallback(data.fallback || '');
    setReplyToVisitor(data.replyToVisitor !== false);
    setAgent(data.agent || '');
    setAgents(data.agents || []);
    setMail(data.mail || null);
    setError('');
  };

  useEffect(() => {
    if (!isBackendConfigured()) { setError('The API is not configured.'); return; }
    load().catch((err) => setError(err.message || 'Could not load the desk.'));
  }, []);

  const ticket = (queue || []).find((t) => t.id === selected) || null;

  const pick = (t) => {
    setSelected(t.id);
    setReply(t.replyText || t.draftText || t.answer || '');
  };

  useEffect(() => {
    if (!queue || !selected) return;
    const t = queue.find((x) => x.id === selected);
    if (t) setReply(t.replyText || t.draftText || t.answer || '');
  }, [queue, selected]);

  // Email tickets carry a thread; load it when one is selected.
  useEffect(() => {
    setThread(null);
    if (!selected) return undefined;
    const t = (queue || []).find((x) => x.id === selected);
    if (!t || (t.channel !== 'email' && (t.messageCount || 1) <= 1)) return undefined;
    let on = true;
    apiGet(`/support/tickets/${selected}/thread`).then((d) => { if (on) setThread(d.messages || []); }).catch(() => {});
    return () => { on = false; };
  }, [queue, selected]);

  const saveRoutes = async (e) => {
    e.preventDefault();
    setBusy('routes');
    setNotice('');
    try {
      await apiPost('/support/routes', { routes, fallback, replyToVisitor, agent });
      setNotice('Routing saved. New requests use it within a minute.');
    } catch (err) {
      setError(err.message || 'Could not save routing.');
    } finally {
      setBusy('');
    }
  };

  const sendReply = async () => {
    if (!ticket) return;
    setBusy('reply');
    setNotice('');
    try {
      await apiPost(`/support/tickets/${ticket.id}/reply`, { text: reply });
      setNotice('Reply queued.');
      await load();
    } catch (err) {
      setError(err.message || 'Could not send that reply.');
    } finally {
      setBusy('');
    }
  };

  const close = async () => {
    if (!ticket) return;
    setBusy('close');
    try {
      await apiPost(`/support/tickets/${ticket.id}/close`, {});
      await load();
    } catch (err) {
      setError(err.message || 'Could not close that.');
    } finally {
      setBusy('');
    }
  };

  return (
    <div className="cs-store" data-nova-note="The support desk: every request from the site, signed-in accounts and the support@ email address, the agent that answers them, and the teammate notified per topic. Pick a request to read the thread and reply.">
      <header className="cs-store-hero">
        <h1>Support desk</h1>
        <p>Requests from the site, from signed-in accounts and from {mail?.address || 'support@nventr.io'}. Each one is answered by the support agent, and the teammate for that topic is notified.</p>
        {mail && (
          <p className="cs-help">
            Email channel: {mail.enabled ? `on · last checked ${when(mail.lastPollAt) || 'never'} · ${mail.filed || 0} filed` : 'off (config/app support.mail.enabled)'}
            {mail.lastError ? ` · last error: ${mail.lastError}` : ''}
          </p>
        )}
      </header>
      <section className="cs-store-section cs-support-desk">
        {error && <p className="cs-land-micro">{error}</p>}
        <form className="cs-sw-card" onSubmit={saveRoutes}>
          <h3>Who answers, who is notified</h3>
          <div className="cs-field">
            <label>Support agent</label>
            <select className="cs-input" value={agent} onChange={(e) => setAgent(e.target.value)}>
              <option value="">Knowledge corpus directly (no agent)</option>
              {agents.map((a) => <option key={a.key} value={a.key}>{a.name}</option>)}
            </select>
            <p className="cs-help">A deployed agent from the nventr org answers with its own prompt, knowledge scope and read tools. Replies still follow the help-desk ladder: cited answers go out, billing and sales wait for a person.</p>
          </div>
          <p className="cs-help">Leave a topic blank to use the fallback. A blank fallback uses the sales inbox.</p>
          {TOPICS.map(([id, label]) => (
            <div className="cs-field" key={id}>
              <label>{label}</label>
              <input className="cs-input" type="email" value={routes[id] || ''} placeholder="name@nventr.ai" onChange={(e) => setRoutes((r) => ({ ...r, [id]: e.target.value }))} />
            </div>
          ))}
          <div className="cs-field">
            <label>Fallback</label>
            <input className="cs-input" type="email" value={fallback} placeholder="sales inbox" onChange={(e) => setFallback(e.target.value)} />
          </div>
          <label className="cs-help" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input type="checkbox" checked={replyToVisitor} onChange={(e) => setReplyToVisitor(e.target.checked)} />
            Email the cited answer to the person who wrote in
          </label>
          <button className="cs-btn cs-btn-primary" type="submit" disabled={busy === 'routes'} style={{ marginTop: 12 }}>
            {busy === 'routes' ? 'Saving…' : 'Save routing'}
          </button>
          {notice && <p className="cs-help">{notice}</p>}
        </form>

        <div className="cs-support-split">
          <div className="cs-sw-card">
            <h3>Queue</h3>
            {!queue && <p className="cs-help">Loading…</p>}
            {queue && queue.length === 0 && <p className="cs-help">No requests yet.</p>}
            <ul className="cs-support-list">
              {(queue || []).map((t) => (
                <li key={t.id}>
                  <button type="button" className={`cs-support-row${t.id === selected ? ' is-on' : ''}`} onClick={() => pick(t)}>
                    <strong>{t.subject || t.message?.slice(0, 80) || 'Request'}</strong>
                    <span className="cs-help">{t.channel === 'email' ? 'Email' : 'Web'} · {t.topicLabel || 'New'} · {t.audience === 'member' ? 'Signed in' : 'Public'} · {t.status}{(t.messageCount || 1) > 1 ? ` · ${t.messageCount} messages` : ''}</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
          <div className="cs-sw-card">
            {!ticket && <p className="cs-help">Select a request. <Link to="/support">Open the public form</Link></p>}
            {ticket && (
              <>
                <h3>{ticket.subject || 'Request'}</h3>
                <p className="cs-help">
                  {ticket.name} &lt;{ticket.email}&gt; · {ticket.audience === 'member' ? 'Signed in' : 'Public'}
                  {ticket.orgName ? ` · ${ticket.orgName}` : ''} · {when(ticket.createdAt)}
                </p>
                <p className="cs-help">
                  {ticket.channel === 'email' ? 'Email' : 'Web form'} · Notified: {ticket.assigneeEmail || 'nobody yet'}{ticket.routeRule ? ` (${ticket.routeRule})` : ''}
                  {ticket.draftVia === 'agent' ? ` · answered by ${agents.find((a) => a.key === ticket.draftAgent)?.name || ticket.draftAgent}` : ''}
                  {ticket.agentTurns ? ` · ${ticket.agentTurns} automatic ${ticket.agentTurns === 1 ? 'reply' : 'replies'}` : ''}
                </p>
                {thread ? (
                  <ol className="cs-support-thread" style={{ listStyle: 'none', padding: 0, margin: '0 0 12px' }}>
                    {thread.map((m, i) => (
                      <li key={m.id || i} style={{ margin: '0 0 12px', paddingLeft: m.dir === 'out' ? 24 : 0 }}>
                        <p className="cs-help" style={{ margin: 0 }}>
                          {m.dir === 'out' ? `nventr support (${m.by || 'desk'})` : `${m.from?.name || ticket.name} <${m.from?.email || ticket.email}>`} · {when(m.at)}
                        </p>
                        <p style={{ whiteSpace: 'pre-wrap', margin: '4px 0 0' }}>{m.text}</p>
                      </li>
                    ))}
                  </ol>
                ) : (
                  <p style={{ whiteSpace: 'pre-wrap' }}>{ticket.message}</p>
                )}
                <div className="cs-field">
                  <label>Reply</label>
                  <textarea className="cs-input" rows={8} value={reply} onChange={(e) => setReply(e.target.value)} />
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button className="cs-btn cs-btn-primary" type="button" disabled={busy === 'reply'} onClick={sendReply}>
                    {busy === 'reply' ? 'Sending…' : ticket.channel === 'email' ? 'Reply in the thread' : 'Email this reply'}
                  </button>
                  {ticket.status !== 'closed' && (
                    <button className="cs-btn cs-btn-outline" type="button" disabled={busy === 'close'} onClick={close}>
                      Close
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
